import {
  DateRangePickerValue,
  FieldWrapper,
  InputDateRangePicker,
} from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { ComponentPropsWithRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'utils/hookForm';

export type HookFormDateRangePickerValue = Record<
  keyof DateRangePickerValue,
  string
>;

/**
 * Represents the React Hook Form variant of the {@link InputDateRangePicker}
 * component.
 */
export const HookFormDateRangePicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    ComponentPropsWithRef<typeof InputDateRangePicker>
  >,
): JSX.Element => {
  const {
    controller: {
      field: { onBlur, value: fieldValue, ...field },
    },
    controllerProps: { name },
    fieldWrapperProps,
  } = useHookFormFieldWrapper(props);

  const value = fieldValue as HookFormDateRangePickerValue;

  /** Handles the date selection. */
  const handleChange = (value: DateRangePickerValue): void => {
    props.componentProps?.onChange?.(value);

    field.onChange?.({
      startDate: value.startDate?.toISOString() ?? '',
      localStartDate: value.localStartDate?.toISOString() ?? '',
      endDate: value.endDate?.toISOString() ?? '',
      localEndDate: value.localEndDate?.toISOString() ?? '',
      timezone: value?.timezone ?? '',
      localTimezone: value?.localTimezone ?? '',
    });
  };

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <InputDateRangePicker
        {...props.componentProps}
        {...field}
        name={name}
        onChange={handleChange}
        value={{
          startDate: moment(value?.localStartDate, moment.ISO_8601).isValid()
            ? moment(value.localStartDate, moment.ISO_8601)
            : null,
          endDate: moment(value?.localEndDate, moment.ISO_8601).isValid()
            ? moment(value.localEndDate, moment.ISO_8601)
            : null,
        }}
        timezone={value?.timezone}
        aria-disabled={fieldWrapperProps.disabled}
        disabled={fieldWrapperProps.disabled}
        aria-required={fieldWrapperProps.required}
        required={fieldWrapperProps.required}
        dropdownProps={{
          ...(props.componentProps?.dropdownProps ?? {}),
          onClose: onBlur,
        }}
      />
    </FieldWrapper>
  );
};
